'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

// Backend integration point: GET /api/member/mariasantos/orders?limit=8
const recentOrders = [
  { id: 'ord-2026-0841', customer: 'Andrea Garcia', product: 'Lumière Radiance Serum', amount: 72, pv: 45, status: 'Delivered', date: 'Aug 24, 2026', items: 1 },
  { id: 'ord-2026-0839', customer: 'Sophia Reyes', product: 'VitaCore Daily Essentials', amount: 104, pv: 70, status: 'Shipped', date: 'Aug 23, 2026', items: 2 },
  { id: 'ord-2026-0835', customer: 'Lisa Tan', product: 'Harmonie Wellness Tea', amount: 27, pv: 18, status: 'Processing', date: 'Aug 22, 2026', items: 1 },
  { id: 'ord-2026-0831', customer: 'Carmen Flores', product: 'Éclat Brightening Mask', amount: 76, pv: 50, status: 'Confirmed', date: 'Aug 21, 2026', items: 2 },
  { id: 'ord-2026-0828', customer: 'Mei Lin', product: 'Equilibre Balance Complex', amount: 62, pv: 40, status: 'Delivered', date: 'Aug 20, 2026', items: 1 },
  { id: 'ord-2026-0824', customer: 'Rosa Mendoza', product: 'Silk Body Ritual Oil', amount: 45, pv: 30, status: 'Delivered', date: 'Aug 19, 2026', items: 1 },
  { id: 'ord-2026-0819', customer: 'Ingrid Larsen', product: 'Lumière Radiance Serum', amount: 144, pv: 90, status: 'Pending', date: 'Aug 18, 2026', items: 2 },
  { id: 'ord-2026-0812', customer: 'Fatima Hassan', product: 'VitaCore Daily Essentials', amount: 52, pv: 35, status: 'Cancelled', date: 'Aug 17, 2026', items: 1 },
];

const statusConfig: Record<string, { label: string; className: string }> = {
  Delivered: { label: 'Delivered', className: 'status-active' },
  Shipped: { label: 'Shipped', className: 'status-processing' },
  Processing: { label: 'Processing', className: 'status-processing' },
  Confirmed: { label: 'Confirmed', className: 'bg-violet-100 text-violet-700 dark:bg-violet-950/50 dark:text-violet-400' },
  Pending: { label: 'Pending', className: 'status-pending' },
  Cancelled: { label: 'Cancelled', className: 'status-inactive' },
};

export default function RecentOrders() {
  const [search, setSearch] = useState('');

  const filtered = recentOrders.filter(o =>
    o.customer.toLowerCase().includes(search.toLowerCase()) ||
    o.id.toLowerCase().includes(search.toLowerCase()) ||
    o.product.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="card-elevated rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-border">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Recent Orders</h3>
          <p className="text-xs text-muted-foreground mt-0.5">From your Richésse store</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              placeholder="Search orders..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="richesse-input pl-8 py-1.5 text-xs w-44"
            />
          </div>
          <button className="text-xs text-primary font-medium hover:text-primary-dark transition-colors">
            View All
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted/30">
              {['Order', 'Customer', 'Product', 'Amount', 'PV', 'Status', 'Date'].map(col => (
                <th key={`orders-col-${col}`} className="text-left px-4 py-3 text-[11px] font-semibold tracking-wider uppercase text-muted-foreground whitespace-nowrap">
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((order, i) => (
              <tr
                key={order.id}
                className={`border-b border-border/50 hover:bg-muted/40 transition-colors cursor-pointer ${
                  i % 2 === 0 ? '' : 'bg-muted/10'
                }`}
              >
                <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{order.id}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-gradient-gold flex items-center justify-center flex-shrink-0">
                      <span className="text-[9px] font-bold text-white">{order.customer.charAt(0)}</span>
                    </div>
                    <span className="text-xs font-medium text-foreground whitespace-nowrap">{order.customer}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground max-w-[140px] truncate">{order.product}</td>
                <td className="px-4 py-3 text-xs font-semibold text-foreground tabular-nums">${order.amount}</td>
                <td className="px-4 py-3 text-xs font-medium text-primary tabular-nums">{order.pv}</td>
                <td className="px-4 py-3">
                  <span className={`richesse-badge text-[10px] ${statusConfig[order.status]?.className}`}>
                    {statusConfig[order.status]?.label}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">{order.date}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <Icon name="ClipboardDocumentListIcon" size={32} className="text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm font-medium text-muted-foreground">No orders match your search</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-5 py-3 border-t border-border flex items-center justify-between">
        <p className="text-xs text-muted-foreground">Showing {filtered.length} of {recentOrders.length} orders</p>
        <div className="flex items-center gap-1">
          {['1', '2', '3'].map(p => (
            <button key={`orders-page-${p}`} className={`w-7 h-7 rounded text-xs font-medium transition-colors ${p === '1' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'}`}>
              {p}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}